class RiseProductCommunity extends HTMLElement {
  #index = 0;
  #pointerId = null;
  #startX = 0;
  #deltaX = 0;
  #dragging = false;
  #dragMoved = false;
  #modal = null;
  #modalFrame = null;
  #modalVideo = null;
  #previouslyFocused = null;

  connectedCallback() {
    this.track = this.querySelector('[data-rise-pc-track]');
    this.viewport = this.querySelector('[data-rise-pc-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-rise-pc-slide]'));
    this.pills = Array.from(this.querySelectorAll('[data-rise-pc-pill]'));
    this.cards = Array.from(this.querySelectorAll('[data-rise-pc-card]'));
    this.arrows = this.querySelector('[data-rise-pc-arrows]');
    this.prevButton = this.querySelector('[data-rise-pc-prev]');
    this.nextButton = this.querySelector('[data-rise-pc-next]');
    this.#modal = this.querySelector('[data-rise-pc-modal]');
    this.#modalFrame = this.querySelector('[data-rise-pc-modal-frame]');
    this.#modalVideo = this.querySelector('[data-rise-pc-modal-video]');

    if (!this.track || this.slides.length === 0) return;

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        this.goTo(Number(pill.dataset.slide || 0));
      });
    });

    this.prevButton?.addEventListener('click', () => this.goTo(this.#index - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.#index + 1));

    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (this.#dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this.#openVideo(card);
      });
    });

    this.#modal?.querySelector('[data-rise-pc-modal-close]')?.addEventListener('click', () => this.#closeModal());
    this.#modal?.querySelector('[data-rise-pc-modal-backdrop]')?.addEventListener('click', () => this.#closeModal());

    this.viewport?.addEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.addEventListener('pointermove', this.#onPointerMove);
    this.viewport?.addEventListener('pointerup', this.#onPointerUp);
    this.viewport?.addEventListener('pointercancel', this.#onPointerUp);
    this.viewport?.addEventListener('pointerleave', this.#onPointerUp);
    window.addEventListener('resize', this.#onResize, { passive: true });
    document.addEventListener('keydown', this.#onKeyDown);

    this.#index = 0;
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('keydown', this.#onKeyDown);
    this.viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.#onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.#onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.#onPointerUp);
    this.viewport?.removeEventListener('pointerleave', this.#onPointerUp);
  }

  #onResize = () => {
    window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(() => {
      this.#index = Math.min(this.#index, this.maxIndex);
      this.update();
    }, 120);
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#modal?.classList.contains('is-open')) {
      this.#closeModal();
    }
  };

  get isDesktop() {
    return window.innerWidth >= 861;
  }

  get columns() {
    return Math.max(1, Number(this.dataset.columns || 4));
  }

  get gap() {
    if (this.isDesktop) return Number(this.dataset.gap || 20);
    return Number(this.dataset.gapMobile || this.dataset.gap || 14);
  }

  get isScrollable() {
    if (!this.isDesktop) return this.slides.length > 1;
    return this.slides.length > this.columns;
  }

  get maxIndex() {
    if (!this.isScrollable) return 0;
    if (this.isDesktop) return Math.max(0, this.slides.length - this.columns);
    return Math.max(0, this.slides.length - 1);
  }

  get slideStep() {
    if (!this.slides[0]) return 0;
    return this.slides[0].getBoundingClientRect().width + this.gap;
  }

  #onPointerDown = (event) => {
    if (!this.isScrollable) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#deltaX = 0;
    this.#dragging = true;
    this.#dragMoved = false;
    this.track.style.transition = 'none';
    this.viewport?.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId || !this.isScrollable) return;
    this.#deltaX = event.clientX - this.#startX;
    if (Math.abs(this.#deltaX) > 8) this.#dragMoved = true;
    const base = this.#index * this.slideStep;
    this.track.style.transform = `translate3d(${-base + this.#deltaX}px, 0, 0)`;
  };

  #onPointerUp = (event) => {
    if (!this.#dragging || (event.pointerId != null && event.pointerId !== this.#pointerId)) return;
    this.#dragging = false;
    this.#pointerId = null;

    if (!this.isScrollable) {
      this.#deltaX = 0;
      return;
    }

    const threshold = Math.min(70, this.slideStep * 0.18);
    if (this.#deltaX <= -threshold) this.goTo(this.#index + 1);
    else if (this.#deltaX >= threshold) this.goTo(this.#index - 1);
    else this.update(true);

    this.#deltaX = 0;
    window.setTimeout(() => {
      this.#dragMoved = false;
    }, 50);
  };

  goTo(index) {
    this.#index = Math.max(0, Math.min(index, this.maxIndex));
    this.update(true);
  }

  update(animate = false) {
    if (!this.track || !this.slides[0]) return;

    this.classList.toggle('is-scrollable', this.isScrollable);

    if (!this.isScrollable) {
      this.track.style.transition = 'none';
      this.track.style.transform = 'translate3d(0, 0, 0)';
      this.#index = 0;
    } else {
      const offset = this.#index * this.slideStep;
      this.track.style.transition = animate ? 'transform 450ms ease' : 'none';
      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    }

    const maxIndex = this.maxIndex;
    this.pills.forEach((pill, index) => {
      pill.hidden = index > maxIndex;
      pill.classList.toggle('is-active', index === this.#index);
    });

    if (this.arrows) this.arrows.hidden = maxIndex === 0;
    if (this.prevButton) this.prevButton.disabled = this.#index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.#index >= maxIndex;
  }

  #parseVideo(card) {
    const kind = (card.dataset.videoKind || '').trim();
    const src = (card.dataset.videoSrc || '').trim();
    if (!kind || !src) return null;

    if (kind === 'file' || kind === 'url') {
      return { type: 'video', src };
    }

    if (kind === 'youtube') {
      return {
        type: 'iframe',
        src: `https://www.youtube.com/embed/${src}?autoplay=1&rel=0`,
      };
    }

    if (kind === 'vimeo') {
      return {
        type: 'iframe',
        src: `https://player.vimeo.com/video/${src}?autoplay=1`,
      };
    }

    return null;
  }

  #openVideo(card) {
    if (!this.#modal) return;

    const parsed = this.#parseVideo(card);
    if (!parsed) return;

    this.#previouslyFocused = document.activeElement;
    this.#clearModalMedia();

    const media = this.#modal.querySelector('.rise-product-community__modal-media');
    media?.classList.toggle('is-file', parsed.type === 'video');

    if (parsed.type === 'video' && this.#modalVideo) {
      this.#modalVideo.hidden = false;
      this.#modalVideo.src = parsed.src;
      this.#modalVideo.play?.().catch(() => {});
      if (this.#modalFrame) this.#modalFrame.hidden = true;
    } else if (this.#modalFrame) {
      this.#modalFrame.hidden = false;
      this.#modalFrame.src = parsed.src;
      if (this.#modalVideo) this.#modalVideo.hidden = true;
    }

    this.#modal.classList.add('is-open');
    this.#modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rise-product-community-modal-open');
    this.#modal.querySelector('[data-rise-pc-modal-close]')?.focus?.();
  }

  #clearModalMedia() {
    if (this.#modalFrame) {
      this.#modalFrame.src = '';
      this.#modalFrame.hidden = true;
    }
    if (this.#modalVideo) {
      this.#modalVideo.pause?.();
      this.#modalVideo.removeAttribute('src');
      this.#modalVideo.load?.();
      this.#modalVideo.hidden = true;
    }
  }

  #closeModal() {
    if (!this.#modal?.classList.contains('is-open')) return;
    this.#clearModalMedia();
    this.#modal.classList.remove('is-open');
    this.#modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rise-product-community-modal-open');
    this.#previouslyFocused?.focus?.();
  }
}

if (!customElements.get('rise-product-community')) {
  customElements.define('rise-product-community', RiseProductCommunity);
}
